import { useTranslate } from "@refinedev/core";
import { Popconfirm } from "antd";
import { useCallback } from "react";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { TemplateType } from "../../../types";
import { MediaResolutionSegmentedButtons } from "../../features/MediaResolutionSegmentedButtons";
import { ThemeSelector } from "../../ThemeSelector";
import { CTAButton } from "../CTAButton";
import { PanelSection } from "../PanelSection";
import { DurationConfigFeature } from "./features/DurationConfigFeature";
import { PlayVideoSoundFeature } from "./features/PlayVideoSoundFeature";

export const LayoutTab = () => {
  const t = useTranslate();

  const {
    templatesData,
    mediaGenerationSettingsData: { mediaGenerationSettings },
  } = useTemplateData();

  const { isFlashProject } = templatesData;

  const {
    currentChapter,
    setTemplates,
    templateState,
    updateTemplateState,
    defaultChapter,
    defaultTransition,
    defaultIntro,
    defaultClosing,
    currentSlide,
    slideNameToTemplateIdMap,
  } = useTemplateEditorStore((store) => ({
    currentChapter: store.currentChapter,
    setTemplates: store.setTemplates,
    templateState: store.templateState,
    updateTemplateState: store.updateTemplateState,
    defaultChapter: store.defaultChapter,
    defaultTransition: store.defaultTransition,
    defaultIntro: store.defaultIntro,
    defaultClosing: store.defaultClosing,
    currentSlide: store.currentSlide,
    slideNameToTemplateIdMap: store.slideNameToTemplateIdMap,
  }));

  const { addTemplateUpdate, addBulkTemplateUpdate } = useTemplatesStatusStore(
    (store) => ({
      addTemplateUpdate: store.addTemplateUpdate,
      addBulkTemplateUpdate: store.addBulkTemplateUpdate,
    })
  );

  const { type: view, templateId } = currentChapter;

  // Flash transition slides report type CHAPTER, so also match by template id
  const isTransitionScene =
    view === TemplateType.TRANSITION ||
    (currentSlide !== undefined &&
      mediaGenerationSettings?.chapters.some(
        (c) =>
          c.transition_template_id === slideNameToTemplateIdMap[currentSlide]
      ));

  const isIntroOrClosing =
    view === TemplateType.INTRODUCTION || view === TemplateType.CLOSING;
  const scene =
    view === TemplateType.INTRODUCTION
      ? templateState.intro
      : templateState.closing;
  // The video's soundtrack sets the scene length, so there's nothing to edit
  const isDurationHidden = isIntroOrClosing && !!scene.playVideoSound;

  const handleApplyToAll = useCallback(() => {
    if (view === TemplateType.CHAPTER) {
      const templateIdsToProcess =
        mediaGenerationSettings?.chapters.reduce((acc, chapter) => {
          if (!chapter.chapter_template_id) return acc;
          if (chapter.chapter_template_id !== templateId) {
            acc.push(chapter.chapter_template_id);
          }
          return acc;
        }, [] as string[]) ?? [];

      setTemplates(templateIdsToProcess, templateState);
      addBulkTemplateUpdate();
    }

    if (view === TemplateType.TRANSITION) {
      const templateIdsToProcess =
        mediaGenerationSettings?.chapters.reduce((acc, chapter) => {
          if (!chapter.transition_template_id) return acc;
          if (chapter.transition_template_id !== templateId) {
            acc.push(chapter.transition_template_id);
          }
          return acc;
        }, [] as string[]) ?? [];

      setTemplates(templateIdsToProcess, templateState);
      addBulkTemplateUpdate();
    }
  }, [
    mediaGenerationSettings?.chapters,
    setTemplates,
    templateId,
    templateState,
    view,
    addBulkTemplateUpdate,
  ]);

  const handleResetToBrandkit = useCallback(() => {
    switch (view) {
      case TemplateType.CHAPTER:
        updateTemplateState(defaultChapter);
        break;
      case TemplateType.TRANSITION:
        updateTemplateState(defaultTransition);
        break;
      case TemplateType.INTRODUCTION:
        updateTemplateState(defaultIntro);
        break;
      case TemplateType.CLOSING:
        updateTemplateState(defaultClosing);
        break;
    }
    addTemplateUpdate();
  }, [
    addTemplateUpdate,
    defaultChapter,
    defaultClosing,
    defaultIntro,
    defaultTransition,
    updateTemplateState,
    view,
  ]);

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.layoutTab.options")}
        subtitle={t(
          "components.templates.settings.layoutTab.aspectRatioDescription"
        )}
      >
        <MediaResolutionSegmentedButtons
          showLabel={false}
          style={{ width: "fit-content" }}
        />
      </PanelSection>

      {isFlashProject && (
        <PanelSection
          title={t("components.templates.settings.themeTab.tabTitle")}
          subtitle={t("components.templates.settings.themeTab.description")}
        >
          <ThemeSelector />
        </PanelSection>
      )}

      {isIntroOrClosing && scene.assetId ? (
        <PanelSection>
          <PlayVideoSoundFeature />
        </PanelSection>
      ) : null}

      {(view !== TemplateType.CHAPTER || isTransitionScene) &&
        !isDurationHidden &&
        templateState.background.bodyView === "Video" && (
          <PanelSection
            title={t("components.templates.settings.layoutTab.duration.title")}
            subtitle={t(
              "components.templates.settings.layoutTab.duration.description"
            )}
          >
            <DurationConfigFeature isTransitionScene={isTransitionScene} />
          </PanelSection>
        )}

      {currentChapter.sectionId !== "" && (
        <PanelSection>
          {(currentChapter.type === TemplateType.CHAPTER ||
            currentChapter.type === TemplateType.TRANSITION) && (
            <Popconfirm
              title={t("components.templates.templatePreview.applyToAll", {
                view: currentChapter.type,
              })}
              onConfirm={handleApplyToAll}
            >
              <CTAButton>
                {t("components.templates.templatePreview.saveAll", {
                  view: currentChapter.type,
                })}
              </CTAButton>
            </Popconfirm>
          )}
          <Popconfirm
            title={t(
              "components.templates.templatePreview.resetToBradkitConfirmation",
              {
                view: currentChapter.type,
              }
            )}
            onConfirm={handleResetToBrandkit}
          >
            <CTAButton>
              {t("components.templates.templatePreview.resetToBradkit")}
            </CTAButton>
          </Popconfirm>
        </PanelSection>
      )}
    </>
  );
};
